import { useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { 
  format, 
  addDays, 
  addMinutes, 
  differenceInMinutes, 
  min, 
  max,
} from "date-fns";
import { toZonedTime, formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography,
  CircularProgress, Tooltip, 
} from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { setJob255CrewRoutes } from "state";
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchFlights } from "state/api";
import { 
  filterCrewRoutes,
  parseDate,
  getDateFromDateTime,
  getTimeFromDateTime,
  countTAT,
  countBreakMinutes,
  calcElapsedTime, 
  calcRouteDays,
  calcPickupMinutes,
  calcHotelDays,
  sortMultipleFields,
  omitFields,
  sanitizeDates,
} from "state/helpers";
import { logClientAction } from 'state/logger';
import CustomDataGridToolbar from "components/CustomDataGridToolbar";
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import RouteFilterDialog from 'components/RouteFilterDialog';
import CrewRoutesList from './CrewRoutesList';


const CrewRoutes = () => {
  useAuthCheck();
  const dispatch = useDispatch();
  const timeMode = useSelector((state) => state.timemode);
  const params = useSelector((state) => state.params);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  
  const [fileName, setFileName] = useState("");
  
  const crewRanksData = useSelector((state) => state.crewRanks);
  const crewRoutesData = useSelector((state) => state.job255CrewRoutes); 
    
  const filteredCrewRoutes = useMemo(() => {
    return filterCrewRoutes({ crewRoutes: crewRoutesData, filters, timeMode });
  }, [crewRoutesData, filters, timeMode]);

  const paramsMap = useMemo(
    () => new Map(params?.map(p => [p.param, p.value]) ?? []),
    [params]
  );      
  const MINIMUM_CREW_ROUTE_TAT_MINUTES = Number(paramsMap.get("MINIMUM_CREW_ROUTE_TAT_MINUTES")) || 80;
  const TIMEZONE_OFFSET_MINUTES = Number(paramsMap.get("TIMEZONE_OFFSET_MINUTES")) || 420;
  const DOM_REPORTING_MINUTES = Number(paramsMap.get("DOM_REPORTING_MINUTES")) || 60;
  const INT_REPORTING_MINUTES = Number(paramsMap.get("INT_REPORTING_MINUTES")) || 75;
  const DEBRIEFING_MINUTES = Number(paramsMap.get("DEBRIEFING_MINUTES")) || 15;
  const PICK_UP_MINUTES = Number(paramsMap.get("PICK_UP_MINUTES")) || 90;
  const MINIMUM_PERDIEM_MINUTES = Number(paramsMap.get("MINIMUM_PERDIEM_MINUTES")) || 360;
  const MINIMUM_CREW_REST_MINUTES = Number(paramsMap.get("MINIMUM_CREW_REST_MINUTES")) || 600;
  
  const excludedFields = [
    "depGate",
    "depStand",
    "arrGate",
    "arrStand",
    "fcCount",
    "ccCount",
    "crewCount",
    "cLounge",
    "hotelDays",
    "delayRemarks",
    "departureType",
    "arrivalType",                
    "expectedPaxPerCategory",
    "paxPerCategory",
    "expectedNoOfPax",
    "actualNoOfPax",
    "noOfPax",
    "capacity",
    "loadFactor",
    "depDelaysCode",
    "arrDelaysCode",
    "preArrDelaysTime",
    "depTimeDiff",
    "arrTimeDiff",
    "btDiff",
    "scheduleTat",
    "estimatedTat",
    "actualTat",
    "tat",
    "tatDiff",
    "actualEstimatedTatDiff",
    "isGroundTimeRecovery",
    "isBlockTimeRecovery",
    "taxiOut",
    "taxiIn",
    "airMinutes",
    "blockMinutes",
    "airHours",
    "cancelledReason",
    "tdDiff", 
    "taDiff",
    "routeCount",
    "crewRoutes",
    "createdAt",
    "updatedAt",
  ];

  const splitCrewRouteRow = (line, index) => {
    return {
      index,
      routeNo: line.substring(0, 5).trim(),
      routeDate: line.substring(5, 13).trim(),
      flightDate: line.substring(13, 21).trim(),
      flightCarrier: line.substring(21, 25).trim(),
      flightNo: line.substring(25, 29).trim(),
      flightNoPostFix: line.substring(29, 30).trim(),
      dep: line.substring(30, 34).trim(),
      arr: line.substring(34, 38).trim(),
  
      std: line.substring(38, 42).trim(),  
      sta: line.substring(42, 46).trim(),  
      dhd: line.substring(46, 47).trim(),  
  
      rsv1: line.substring(47, 50).trim(),
      rsv2: line.substring(50, 53).trim(),

      pd1: line.substring(53, 55).trim(),
      pd2: line.substring(55, 57).trim(),
      pd3: line.substring(57, 59).trim(),
      pd4: line.substring(59, 61).trim(),
      
      cc1: line.substring(61, 63).trim(),
      cc2: line.substring(63, 65).trim(),
      cc3: line.substring(65, 67).trim(),
      cc4: line.substring(67, 69).trim(),
      cc5: line.substring(69, 71).trim(),
      cc6: line.substring(71, 73).trim(),
      cc7: line.substring(73, 75).trim(),
      cc8: line.substring(75, 77).trim(),
      cc9: line.substring(77, 79).trim(),
      cc10: line.substring(79, 81).trim(),
      cc11: line.substring(81, 83).trim(),
      cc12: line.substring(83, 85).trim(),
      cc13: line.substring(85, 87).trim(),
      cc14: line.substring(87, 89).trim(),
      cc15: line.substring(89, 91).trim(),
      cc16: line.substring(91, 93).trim(),
      cc17: line.substring(93, 95).trim(),
      cc18: line.substring(95, 97).trim(),
      cc19: line.substring(97, 99).trim(),
      cc20: line.substring(99, 101).trim(),
    };
  };
  
  const crewRanks = {
    CP: 'pd1',
    FO: 'pd2',
    IOE: 'pd3',
    CM: 'cc1',
    SA: 'cc2',
    BA: 'cc6',
    CA: 'cc3',
    IP: 'cc4',
    TC: 'cc5',
  };
  
  const filterCrewRoutesByDate = (crewRoutes, startDate, endDate) => {
    return crewRoutes.filter(route => {
      const compareDate =
        timeMode === "UTC"
          ? new Date(route.routeDate)           // routeDate nên là ISO string
          : new Date(route.routeDateLocalBase); // Local Base

      return compareDate >= startDate && compareDate <= endDate;
    });
  };

  const handleUpload = (event) => {
    try {
      const files = event.target.files;
      if (files.length === 0) return;
  
      setFileName(files[0].name);
      setLoading(true);
      
      const file = files[0];
      if (!file.name.endsWith(".txt")) {
        toast.error("Please upload a .txt file.");
        setLoading(false);
        return;
      }  
      
      
      const reader = new FileReader();
      reader.onload = async function (e) {
        const fileContent = e.target.result.replace(/^\uFEFF/, ''); // Remove BOM if present
    
        const lines = fileContent
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        if (lines.length === 0) return;

        let splittedRows = [];  
        lines?.forEach((line, index) => {
          try {
            const rowArray = splitCrewRouteRow(line, index);

            // Remove Standby, FR, RR routes
            if (rowArray.dep !== rowArray.arr) {
              splittedRows.push(rowArray);
            }
          } catch (error) {
            toast.error(`Error parsing line ${line}: ${error.message}`);
          }
        });
        
        let flights;
        try {
          const flightDates = splittedRows.map(leg => parseDate(leg.flightDate, 'ddMMyyyy'));
          const minDate = min(flightDates);
          const maxDate = max(flightDates);

          flights = await fetchFlights(addDays(minDate, -1), addDays(maxDate, 1));
        } catch (error) {
          toast.error(error.message || "Failed to fetch flights!");
          setLoading(false);
          return;
        }
        
        const flightMap = new Map();
        flights.forEach(flight => {
          const key = `${format(flight.flightDate, 'yyyy-MM-dd')}-${flight.flightDesc}-${flight.dep}`;
          flightMap.set(key, flight);
        });
        
        const sumFields = (row, prefix, count) =>
          Array.from({ length: count }, (_, i) => Number(row[`${prefix}${i + 1}`] ?? 0))
            .reduce((sum, val) => sum + val, 0);
              
        const processedFlights = splittedRows.map((row) => {
          const flightDate = parseDate(row.flightDate, 'ddMMyyyy');          
          const flightDesc = `${row.flightCarrier}${row.flightNo}${row.flightNoPostFix}`;
          const flightKey = `${format(flightDate, 'yyyy-MM-dd')}-${flightDesc}-${row.dep}`;          
          const routeDate = parseDate(row.routeDate, 'ddMMyyyy');          
          const routeNo = row.routeNo;          
          const pax = row.dhd === 'Y' ? '*' : null;          
          // const std = insertColonToTime(row.std);
          // const sta = insertColonToTime(row.sta);    
          const mappedRanks = Object.fromEntries(
            Object.entries(crewRanks).map(([rank, key]) => [rank, Number(row[key] ?? 0)])
          );

          const flight = flightMap.get(flightKey);          
          if (!flight) {
            console.warn(`Flight not found for key: ${flightKey}`);
            return null;
          }
          const leanedFlight = omitFields(flight, excludedFields);

          const fcCount = sumFields(row, "pd", 4);
          const ccCount = sumFields(row, "cc", 20);
          const crewCount = fcCount + ccCount;
          
          return {                
            ...leanedFlight,
            ...mappedRanks,
            routeDate,     
            routeNo,  
            pax,
            fcCount,
            ccCount,
            crewCount,
          }
        }).filter(Boolean);;
        
        
        const crewRoutesMap = processedFlights.reduce((acc, flight) => {
          const routeKey = `${flight.routeDate}-${flight.routeNo}`;

          if (!acc[routeKey]) {
            acc[routeKey] = {
              key: routeKey,
              routeDate: flight.routeDate,
              routeDateLocalBase: flight.flightDateLocalBase || getDateFromDateTime(flight.fullStdLocalBase),
              routeNo: flight.routeNo,
              blockHours: 0,
              sectors: 0,
              posHours: 0,
              posSectors: 0,
              dhdHours: 0,
              dhdSectors: 0,
              trainingHours: 0,
              trainingSectors: 0,
              elapsedHours: 0,
              routeDays: 0,
              perdiemMinutes: 0,
              cLounge: 0,
              hotelDays: 0,
              paxCount: 0,
              crewCount: 0,
              fcCount: 0,
              ccCount: 0,
              ...Object.fromEntries(crewRanksData.map((r) => [r, 0])),
              routeDetails: [],
            };
          }

          acc[routeKey].routeDetails.push(flight);
          return acc;
        }, {});

        Object.values(crewRoutesMap).forEach((route) => {
          const sortedFlights = route.routeDetails.sort((a, b) => {
            const dateA = new Date(a.fullEtd);
            const dateB = new Date(b.fullEtd);
            return dateA - dateB;
          });
            
          sortedFlights.forEach((flight, index) => {
            let scheduleTat = null;
            let estimatedTat = null;
            let actualTat = null;
            let diffRegScheduleTat = null;
            let diffRegEstimatedTat = null;
            let diffRegActualTat = null;
            let scheduleBreakMinutes = null;
            let estimatedBreakMinutes = null;
            let actualBreakMinutes = null;
            
            if (flight.sectorType === 'Domestic') {
              flight.fullRpt = addMinutes(flight.fullStd, -DOM_REPORTING_MINUTES);
              flight.fullRptLocalBase = addMinutes(flight.fullStdLocalBase, -DOM_REPORTING_MINUTES);                        
            } else {
              flight.fullRpt = addMinutes(flight.fullStd, -INT_REPORTING_MINUTES);
              flight.fullRptLocalBase = addMinutes(flight.fullStdLocalBase, -INT_REPORTING_MINUTES);
            }
            flight.rpt = getTimeFromDateTime(flight.fullRpt);
            flight.rptLocalBase = getTimeFromDateTime(flight.fullRptLocalBase);

            const pickupMinutes = calcPickupMinutes(flight.sectorType, flight.dep, PICK_UP_MINUTES);
            flight.fullPickup = addMinutes(flight.fullStd, -pickupMinutes);
            flight.fullPickupLocalBase = addMinutes(flight.fullStdLocalBase, -pickupMinutes);
            flight.pickup = getTimeFromDateTime(route.fullPickup);
            flight.pickupLocalBase = getTimeFromDateTime(route.fullPickupLocalBase);

            flight.fullEod = addMinutes(flight.fullSta, DEBRIEFING_MINUTES);
            flight.fullEodLocalBase = addMinutes(flight.fullStaLocalBase, DEBRIEFING_MINUTES);                    
            flight.eod = getTimeFromDateTime(route.fullEod);
            flight.eodLocalBase = getTimeFromDateTime(route.fullEodLocalBase);


            if (index === 0) {
              flight.cLounge = 0;
              flight.hotelDays = 0;

              route.routeDateLocalBase = getDateFromDateTime(flight.fullStdLocalBase);
              route.flightNo = `${flight.flightNo}`;
              route.crewRoute = `${flight.dep}-${flight.arr}`;
              route.acType = flight.acType;
              route.flightReg = flight.flightReg;
              route.dep = flight.dep;
              route.arr = flight.arr;
              route.std = flight.std;
              route.sta = flight.sta;
              route.stdLocalBase = flight.stdLocalBase;
              route.staLocalBase = flight.staLocalBase;
              route.fullStd = flight.fullStd;
              route.fullSta = flight.fullSta;
              route.fullStdLocalBase = flight.fullStdLocalBase;
              route.fullStaLocalBase = flight.fullStaLocalBase;                    
              route.etd = flight.etd;
              route.eta = flight.eta;
              route.etdLocalBase = flight.etdLocalBase;
              route.etaLocalBase = flight.etaLocalBase;
              route.fullEtd = flight.fullEtd;
              route.fullEta = flight.fullEta;
              route.fullEtdLocalBase = flight.fullEtdLocalBase;
              route.fullEtaLocalBase = flight.fullEtaLocalBase;
              route.atd = flight.atd;
              route.ata = flight.ata;
              route.atdLocalBase = flight.atdLocalBase;
              route.ataLocalBase = flight.ataLocalBase;
              route.fullAtd = flight.fullAtd;
              route.fullAta = flight.fullAta;
              route.fullAtdLocalBase = flight.fullAtdLocalBase;
              route.fullAtaLocalBase = flight.fullAtaLocalBase;
              route.depDelaysTime = flight.depDelaysTime;
              
              if (flight.sectorType === 'Domestic') {
                route.fullRpt = addMinutes(flight.fullStd, -DOM_REPORTING_MINUTES);
                route.fullRptLocalBase = addMinutes(flight.fullStdLocalBase, -DOM_REPORTING_MINUTES);                        
              } else {
                route.fullRpt = addMinutes(flight.fullStd, -INT_REPORTING_MINUTES);
                route.fullRptLocalBase = addMinutes(flight.fullStdLocalBase, -INT_REPORTING_MINUTES);
              }
              route.rpt = getTimeFromDateTime(route.fullRpt);
              route.rptLocalBase = getTimeFromDateTime(route.fullRptLocalBase);

              // const pickupMinutes = calculatePickupMinutes(flight.sectorType, flight.dep, PICK_UP_MINUTES);
              route.fullPickup = addMinutes(flight.fullStd, -pickupMinutes);
              route.fullPickupLocalBase = addMinutes(flight.fullStdLocalBase, -pickupMinutes);
              route.pickup = getTimeFromDateTime(route.fullPickup);
              route.pickupLocalBase = getTimeFromDateTime(route.fullPickupLocalBase);

              route.fullEod = addMinutes(flight.fullSta, DEBRIEFING_MINUTES);
              route.fullEodLocalBase = addMinutes(flight.fullStaLocalBase, DEBRIEFING_MINUTES);                    
              route.eod = getTimeFromDateTime(route.fullEod);
              route.eodLocalBase = getTimeFromDateTime(route.fullEodLocalBase);
            } else {
              route.flightNo += `.${flight.flightNo}`;
              route.crewRoute += `-${flight.arr}`;
              route.arr = flight.arr;
              route.sta = flight.sta;
              route.staLocalBase = flight.staLocalBase;
              route.fullSta = flight.fullSta;
              route.fullStaLocalBase = flight.fullStaLocalBase;
              route.eta = flight.eta;
              route.etaLocalBase = flight.etaLocalBase;
              route.fullEta = flight.fullEta;
              route.fullEtaLocalBase = flight.fullEtaLocalBase;
              route.ata = flight.ata;
              route.ataLocalBase = flight.ataLocalBase;
              route.fullAta = flight.fullAta;
              route.fullAtaLocalBase = flight.fullAtaLocalBase;
              route.arrDelaysTime = flight.arrDelaysTime;
              route.fullEod = addMinutes(flight.fullSta, DEBRIEFING_MINUTES);
              route.fullEodLocalBase = addMinutes(flight.fullStaLocalBase, DEBRIEFING_MINUTES);
              route.eod = getTimeFromDateTime(route.fullEod);
              route.eodLocalBase = getTimeFromDateTime(route.fullEodLocalBase);
                              
              if (flight.dep === sortedFlights[index - 1].arr) {
                if (flight.fullStd && sortedFlights[index - 1].fullSta) {
                  scheduleTat = countTAT(flight.fullStd, sortedFlights[index - 1].fullSta);
                  
                  const fullPreEod = addMinutes(sortedFlights[index - 1].fullSta, DEBRIEFING_MINUTES);
                  let fullCurRpt = addMinutes(flight.fullStd, -DOM_REPORTING_MINUTES);
                  if (flight.sectorType === 'International') {
                    fullCurRpt = addMinutes(flight.fullStd, -INT_REPORTING_MINUTES);
                  }
                  scheduleBreakMinutes = Math.max(countBreakMinutes(fullCurRpt, fullPreEod), 0);

                  if(flight.flightReg !== sortedFlights[index - 1].flightReg && scheduleTat < MINIMUM_CREW_ROUTE_TAT_MINUTES) {
                    diffRegScheduleTat = scheduleTat;
                  }
                }
                if (flight.fullEtd && sortedFlights[index - 1].fullEta) {
                  estimatedTat = countTAT(flight.fullEtd, sortedFlights[index - 1].fullEta);
                  
                  const fullPreEod = addMinutes(sortedFlights[index - 1].fullEta, DEBRIEFING_MINUTES);
                  let fullCurRpt = addMinutes(flight.fullEtd, -DOM_REPORTING_MINUTES);
                  if (flight.sectorType === 'International') {
                    fullCurRpt = addMinutes(flight.fullEtd, -INT_REPORTING_MINUTES);
                  }
                  estimatedBreakMinutes = Math.max(countBreakMinutes(fullCurRpt, fullPreEod), 0);

                  if(flight.flightReg !== sortedFlights[index - 1].flightReg && estimatedTat < MINIMUM_CREW_ROUTE_TAT_MINUTES) {
                    diffRegEstimatedTat = estimatedTat;
                  }
                }
                if (flight.fullAtd && sortedFlights[index - 1].fullAta) {
                  actualTat = countTAT(flight.fullAtd, sortedFlights[index - 1].fullAta);

                  const fullPreEod = addMinutes(sortedFlights[index - 1].fullAta, DEBRIEFING_MINUTES);
                  let fullCurRpt = addMinutes(flight.fullAtd, -DOM_REPORTING_MINUTES);
                  if (flight.sectorType === 'International') {
                    fullCurRpt = addMinutes(flight.fullAtd, -INT_REPORTING_MINUTES);
                  } 
                  actualBreakMinutes = Math.max(countBreakMinutes(fullCurRpt, fullPreEod), 0);

                  if(flight.flightReg !== sortedFlights[index - 1].flightReg && actualTat < MINIMUM_CREW_ROUTE_TAT_MINUTES) {
                    diffRegActualTat = actualTat;
                  }
                }
              }

              const tat = actualTat || estimatedTat || scheduleTat;
              const diffRegTat = diffRegActualTat || diffRegEstimatedTat || diffRegScheduleTat;
              const breakMinutes = scheduleBreakMinutes || estimatedBreakMinutes || actualBreakMinutes;

              flight.scheduleTat = scheduleTat;
              flight.estimatedTat = estimatedTat;
              flight.actualTat = actualTat;
              flight.tat = tat;
              flight.diffRegScheduleTat = diffRegScheduleTat;
              flight.diffRegEstimatedTat = diffRegEstimatedTat;
              flight.diffRegActualTat = diffRegActualTat;
              flight.diffRegTat = diffRegTat;
              flight.breakMinutes = breakMinutes;
              
              if (tat >= MINIMUM_PERDIEM_MINUTES) {
                flight.perdiemMinutes = tat - MINIMUM_PERDIEM_MINUTES;
              } 
              
              if (breakMinutes >= 180 && breakMinutes < 360) {
                flight.cLounge = 1;
              } else {
                flight.cLounge = 0;
              }

              if (breakMinutes >= 360) {
                const preEod = toZonedTime(sortedFlights[index - 1].fullEodLocalBase, 'UTC');
                const curPickup = toZonedTime(flight.fullPickupLocalBase, 'UTC');
                flight.hotelDays = Math.min(calcHotelDays(preEod, curPickup), 15);
              } else {
                flight.hotelDays = 0;
              }
            } 

            if (index === sortedFlights.length - 1) {
              route.elapsedHours = calcElapsedTime(route.fullEod, route.fullRpt);
              route.routeDays = calcRouteDays(route.fullEod, route.fullRpt, TIMEZONE_OFFSET_MINUTES);
            }

            if (!flight.pax) {              
              route.blockHours += flight.blockHours || 0;
              route.sectors += 1;
            } else {
              const positioningType = index === sortedFlights.length - 1 || flight.arr !== sortedFlights[index + 1].dep
                ? 'DHD'
                : differenceInMinutes(sortedFlights[index + 1].fullRpt, flight.fullEod) > MINIMUM_CREW_REST_MINUTES 
                  ? 'DHD' 
                  : 'POS';

              if (positioningType === 'POS') {
                route.posHours += flight.blockHours || 0;
                route.posSectors++;
              } else {
                route.dhdHours += flight.blockHours || 0;
                route.dhdSectors++;
              }              
            }   
            
            route.perdiemMinutes += flight.perdiemMinutes || 0;            
            route.cLounge += flight.cLounge || 0;
            route.hotelDays += flight.hotelDays || 0;

            route.paxCount = (route.posSectors + route.dhdSectors) * flight.crewCount;
            route.fcCount = flight.fcCount || 0;
            route.ccCount = flight.ccCount || 0;
            route.crewCount = flight.crewCount || 0;
            Object.keys(crewRanks).forEach(rank => {
              route[rank] = Number(flight[rank] || 0);
            });
          })              
        });

        const sortedCrewRoutes = sortMultipleFields(Object.values(crewRoutesMap), [
          { field: 'fullStd', direction: 'asc' },
          { field: 'routeNo', direction: 'asc' },
        ]);
        
        const filteredRoutes = filterCrewRoutesByDate(
          sortedCrewRoutes,
          startDate,
          endDate,
          timeMode
        );

        const safeCrewRoutes = sanitizeDates(filteredRoutes);        
        dispatch(setJob255CrewRoutes({ job255CrewRoutes: safeCrewRoutes }));

        toast.success("File uploaded successfully.");
      };
  
      reader.onerror = function () {
        setLoading(false);
        toast.error("File reading error!");
      };
  
      reader.readAsText(file);
    } catch (error) {
      setError(error);      
      toast.error(error.message || "Failed to parse crew pairings!");
    } finally {
      setLoading(false);
    }
  };  

  const handleExcelJSDownload = () => {
    const exportToExcel = async (data, fileName = "CrewRoutes.xlsx") => {
      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("CrewRoutes");

        // Add headers dynamically based on keys in the first object of the data array
        if (data.length > 0) {
          const headers = ['No.', ...Object.keys(data[0]).filter(key => key !== '_id')];
          worksheet.addRow(headers);

          // Apply header styles
          headers.forEach((header, index) => {
            const cell = worksheet.getCell(1, index + 1);
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFCC" }, // Light yellow
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }

        // Add data rows, replacing '_id' with row number
        data.forEach((row, index) => {
          const rowData = [index + 1, ...Object.entries(row).filter(([key]) => key !== '_id').map(([, value]) => value)];
          worksheet.addRow(rowData);
        });

        // Apply text wrapping to all cells
        worksheet.eachRow((row) => {
          row.eachCell((cell) => {
            cell.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        // Adjust column widths based on the content
        worksheet.columns.forEach((column) => {
          column.width = column.header
            ? Math.max(
                column.header.length,
                ...column.values.map((v) => (v ? v.toString().length : 30))
              ) + 2 
            : 30;
        });

        // Generate and save the Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
      } catch (error) {
          toast.error("Failed to export to Excel.", error);
      }
    };

    const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
    const fileName = `CrewRoutes_${timestamp}.xlsx`;
    
    if (!filteredCrewRoutes || filteredCrewRoutes.length === 0) {
      toast.warning("No data available to export.");
      return;
    }
    const updatedCrewRoutes = filteredCrewRoutes.map(({ 
      routeDetails, 
      routeDate, 
      routeDateLocalBase, 
      fullPickup, 
      fullRpt, 
      fullEtd, 
      fullEta, 
      fullAtd, 
      fullAta, 
      fullEod, 
      fullPickupLocalBase, 
      fullRptLocalBase, 
      fullEtdLocalBase, 
      fullEtaLocalBase, 
      fullAtdLocalBase, 
      fullAtaLocalBase, 
      fullEodLocalBase, 
      pickupLocalBase, 
      rptLocalBase, 
      etdLocalBase, 
      etaLocalBase, 
      atdLocalBase, 
      ataLocalBase, 
      eodLocalBase, 
      ...rest 
    }) => rest);
    exportToExcel(updatedCrewRoutes, fileName);
  };

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };   
         
  const buildOptions = (data, key) =>
    [...new Set(data.map(item => item[key]))].sort();

  const { 
    acTypeOptions, 
    flightRegOptions, 
    depOptions, 
    arrOptions 
  } = useMemo(() => ({
    acTypeOptions: buildOptions(crewRoutesData, 'acType'),
    flightRegOptions: buildOptions(crewRoutesData, 'flightReg'),
    depOptions: buildOptions(crewRoutesData, 'dep'),
    arrOptions: buildOptions(crewRoutesData, 'arr'),
  }), [crewRoutesData]);
   
  // Define delay thresholds and styles
  const DELAY_THRESHOLDS = useMemo(() => ({
    HIGH: { minutes: 60, color: '#f2292d' },
    MEDIUM: { minutes: 30, color: '#f29129' },
    LOW: { minutes: 15, color: '#d7f229' },
  }), []);
    
  // Centralized animation styles
  const BLINK_ANIMATION = useMemo(() => ({
    animation: 'blink 1s step-start 0s infinite',
    fontWeight: 'bold',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  }), []);
    
  const getBlinkStyles = useCallback((delay, dateStr) => {
    if (!delay || isNaN(delay)) return {};

    let today = new Date();
    if (timeMode === 'UTC') {
      today = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    } else {
      today.setHours(0, 0, 0, 0);
    }

    const routeDate = new Date(dateStr);
    const isTodayOrFuture = routeDate >= today;

    let color;
    if (delay > DELAY_THRESHOLDS.HIGH.minutes) {
      color = DELAY_THRESHOLDS.HIGH.color;
    } else if (delay > DELAY_THRESHOLDS.MEDIUM.minutes) {
      color = DELAY_THRESHOLDS.MEDIUM.color;
    } else if (delay > DELAY_THRESHOLDS.LOW.minutes) {
      color = DELAY_THRESHOLDS.LOW.color;
    } else {
      return {};
    }

    return {
      color,
      ...(isTodayOrFuture && BLINK_ANIMATION),
    };
  }, [timeMode, BLINK_ANIMATION, DELAY_THRESHOLDS]);
  
  const createTimeColumn = useCallback(({ field, headerName, delayField, timeMode }) => ({
    field: timeMode === 'UTC' ? field : `${field}LocalBase`,
    headerName,
    headerClassName: 'theme-column--header',
    width: 88,
    renderCell: ({ row }) => {
      const delay = row[delayField] || 0;
      const dateStr = timeMode === 'UTC' ? row.routeDate : row.routeDateLocalBase;
  
      return (
        <FlexCenter
          height="100%"
          width="100%"
          sx={{
            '@keyframes blink': {
              '60%': { opacity: 0.6 },
            },
          }}
        >
          <Typography
            variant="body2"
            sx={getBlinkStyles(delay, dateStr)}
          >
            {row[timeMode === 'UTC' ? field : `${field}LocalBase`] || ''}
          </Typography>
        </FlexCenter>
      );
    },
  }), [getBlinkStyles]); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      width: 50,
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: 'routeDetails',
      headerName: 'Legs',
      width: isMobile ? 333 : 338,       
      renderCell: (params) => {
        const hasWarningFlight = params.row?.routeDetails?.some(
          (flight) => flight.diffRegScheduleTat !== null && flight.diffRegScheduleTat < MINIMUM_CREW_ROUTE_TAT_MINUTES
        );
        
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
              animation: hasWarningFlight ? 'glow 1.5s ease-in-out infinite' : 'none',
              '@keyframes glow': {
                '0%': { boxShadow: '0 0 0px orange' },
                '50%': { boxShadow: '0 0 10px orange' },
                '100%': { boxShadow: '0 0 0px orange' },
              },
            }}
          >
            {params.row?.routeDetails.map((flight, index) => {     
              const fullAtd = timeMode === 'UTC' 
                ? formatInTimeZone(flight.fullAtd || flight.fullEtd, "UTC", "ddMMMyy") 
                : formatInTimeZone(flight.fullAtdLocalBase || flight.fullEtdLocalBase, "UTC", "ddMMMyy")
              const atd = timeMode === 'UTC' ? (flight?.atd || flight?.etd) : (flight?.atdLocalBase || flight?.etdLocalBase);
              const ata = timeMode === 'UTC' ? (flight?.ata || flight?.eta) : (flight?.ataLocalBase || flight?.etaLocalBase);

              return (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    color: flight.diffRegScheduleTat && flight.diffRegScheduleTat < MINIMUM_CREW_ROUTE_TAT_MINUTES ? theme.palette.warning.main : 'inherit',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      width: '30px',
                    }}
                  >
                    {flight.flightNo}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: '54px',
                    }}
                  >
                    {flight.flightReg || flight.acType}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      width: '45px',
                    }}
                  >
                    {fullAtd}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: '30px',
                    }}
                  >
                    {atd}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: '25px',
                    }}
                  >
                    {flight.dep}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: '25px',
                    }}
                  >
                    {flight.arr}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: '30px',
                    }}
                  >
                    {ata}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'left',
                      width: '8px',
                    }}
                  >
                    {flight.pax}
                  </Typography>
                </Box>
              )}
            )}
          </Box>
        );
      },
    },    
    {
      field: timeMode === 'UTC' ? "routeDate" : "routeDateLocalBase",
      headerName: "RouteDate",
      width: 100, 
      valueFormatter: (params) => {
        return format(new Date(params), "dd/MM/yy");
      },
    },
    {
      field: "routeNo",
      headerName: "RouteNo",
      width: 88,
    },    
    {
      field: "flightNo",
      headerName: "FlightNo",
      width: 220,
    },
    {
      field: "crewRoute",
      headerName: "CrewRoute",
      width: 260,
      cellClassName: "name-column--cell",
    },
    {
      field: "acType",
      headerName: "ACType",
      width: 88,
    },{
      field: "flightReg",
      headerName: "FlightReg",
      width: 100,
    },
    {
      field: "dep",
      headerName: "Dep",
      width: 88,
    },
    {
      field: "arr",
      headerName: "Arr",
      width: 88,
    },      
    // {
    //   field: timeMode === 'UTC' ? "pickup" : "pickupLocalBase",
    //   headerName: "Pickup",
    //   width: 88,
    // },
    createTimeColumn({
      field: 'pickup',
      headerName: 'Pickup',
      delayField: 'depDelaysTime',
      timeMode,
    }), 
    {
      field: timeMode === 'UTC' ? "rpt" : "rptLocalBase",
      headerName: "Rpt",
      width: 88,
    },
    {
      field: timeMode === 'UTC' ? "std" : "stdLocalBase",
      headerName: "Std",
      width: 88,
    },
    {
      field: timeMode === 'UTC' ? "sta" : "staLocalBase",
      headerName: "Sta",
      width: 88,
    }, 
    // {
    //   field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
    //   headerName: "Etd",
    //   width: 88,
    // },
    // {
    //   field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
    //   headerName: "Eta",
    //   width: 88,
    // },
    createTimeColumn({
      field: 'etd',
      headerName: 'Etd',
      delayField: 'depDelaysTime',
      timeMode,
    }),
    createTimeColumn({
      field: 'eta',
      headerName: 'Eta',
      delayField: 'arrDelaysTime',
      timeMode,
    }),
    {
      field: timeMode === 'UTC' ? "atd" : "atdLocalBase",
      headerName: "Atd",
      width: 88,
    },
    {
      field: timeMode === 'UTC' ? "ata" : "ataLocalBase",
      headerName: "Ata",
      width: 88,
    },
    createTimeColumn({
      field: 'eod',
      headerName: 'Eod',
      delayField: 'arrDelaysTime',
      timeMode,
    }),    
    {
      field: "elapsedHours",
      headerName: "ElapsedHours",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(2) : 0;
      },
    },
    {
      field: "routeDays",
      headerName: "RouteDays",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    }, 
    {
      field: "blockHours",
      headerName: "BH",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(2) : 0;
      },
    },
    {
      field: "sectors",
      headerName: "Sectors",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },
    {
      field: "posHours",
      headerName: "PosHours",
      width: 100,   
      align: 'right',
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(2) : 0; 
      },
    },
    {
      field: "posSectors",
      headerName: "PosSectors",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },
    {
      field: "dhdHours",
      headerName: "DhdHours",
      width: 100,   
      align: 'right',
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(2) : 0; 
      },
    },
    {
      field: "dhdSectors",
      headerName: "DhdSectors",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },  
    {
      field: "perdiemMinutes",
      headerName: "PerdiemMinutes",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(0) : 0;
      },
    },
    {
      field: "cLounge",
      headerName: "CLounge",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    }, 
    {
      field: "hotelDays",
      headerName: "HotelDays",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    }, 
    {
      field: "paxCount",
      headerName: "PaxCount",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },
    {
      field: "crewCount",
      headerName: "CrewCount",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },
    {
      field: "fcCount",
      headerName: "FCCount",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },
    {
      field: "ccCount",
      headerName: "CCCount",
      width: 100,
      align: 'right',
      cellClassName: 'right-align-cell',
    },
    ...crewRanksData
      .slice()
      .sort((a, b) => (a.rankPriority ?? 9999) - (b.rankPriority ?? 9999))
      .map(rank => ({
        field: rank.rankCode,
        headerName: rank.rankCode,
        width: 80,
        align: 'right',
        cellClassName: 'right-align-cell',
    })),
  ], [timeMode, theme, isMobile, crewRanksData, MINIMUM_CREW_ROUTE_TAT_MINUTES, createTimeColumn]);

  const hiddenColumns = useMemo(() => {
    return {
      // no: false,
      // routeDate: false,
      // routeDateLocalBase: false,
      // routeNo: false,
      // flightNo: false,
      // crewRoute: false,
      // acType: false,
      // flightReg: false,
      // dep: false,
      // arr: false,
      // pickup: false,
      // pickupLocalBase: false,
      // rpt: false,
      // rptLocalBase: false,
      // std: false,
      // stdLocalBase: false,
      // sta: false,
      // staLocalBase: false,
      etd: false,
      etdLocalBase: false,
      eta: false,
      etaLocalBase: false,
      atd: false,
      atdLocalBase: false,
      ata: false,
      ataLocalBase: false,
      // eod: false,
      // eodLocalBase: false,
      // blockHours: false,
      // sectors: false,
      // posHours: false,
      // posSectors: false,
      // dhdHours: false,
      // dhdSectors: false,
      // trainingHours: false,
      // trainingSectors: false,
      // elapsedHours: false,
      // routeDays: false,
      // perdiemMinutes: false,
      // paxCount: false,
      // crewCount: false,
      // fcCount: false,
      // ccCount: false,    
      ...crewRanksData.reduce((acc, rank) => {
        acc[rank.rankCode] = false; 
        return acc;
      }, {}),
      cLounge: false,
      hotelDays: false,
    };
  }, [crewRanksData]); 
  
  const nonMobileHiddenColumns = useMemo(() => { 
    return {
      // std: false,
      // stdLocalBase: false,
      // sta: false,
      // staLocalBase: false,
      etd: false,
      etdLocalBase: false,
      eta: false,
      etaLocalBase: false,
      atd: false,
      atdLocalBase: false,
      ata: false,
      ataLocalBase: false,
      fcCount: false,
      ccCount: false,    
      ...crewRanksData.reduce((acc, rank) => {
        acc[rank.rankCode] = false; 
        return acc;
      }, {}),
    }
  }, [crewRanksData]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Crew Routes (Job 255)" 
          subtitle={`${loading ? 'Loading...' : 'List of Crew Routes'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>    
            <label htmlFor={!!fileName ? undefined : "upload-file"}>
              <Tooltip title="Upload job 255 file" arrow>
                <span>
                  <StyledButton 
                    component="span" 
                    disabled={!!fileName}
                  >
                    <FileUploadIcon />
                  </StyledButton>
                </span>                
              </Tooltip>
            </label>
            <input
              id="upload-file"
              type="file"
              accept=".txt"
              style={{ display: 'none' }}
              onChange={handleUpload}
            /> 

            <Tooltip title="Download Excel file" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    handleExcelJSDownload();
                    logClientAction("EXPORT_REPORT", { fileName: "CrewRoutes", format: "Excel", startDate, endDate });
                  }}
                >
                  <DownloadIcon />
                </StyledButton>
              </span>
            </Tooltip>
            
            <FilterButton
              filters={filters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}          
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CrewRoutesList
                rows={filteredCrewRoutes || []}
                timeMode={timeMode}
                minCrewRouteTAT={MINIMUM_CREW_ROUTE_TAT_MINUTES}
              />
            ) : (
              <DataGrid
                getRowId={(row) => row.key}
                rows={filteredCrewRoutes || []}
                columns={columns}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 100,
                    },
                  },
                  columns: {
                    columnVisibilityModel: !isDesktop ? hiddenColumns : nonMobileHiddenColumns,
                  },
                }}
                loading={loading}
                showToolbar
                slots={{ toolbar: () => <CustomDataGridToolbar isExpanded={true} /> }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen &&
        <RouteFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          acTypeOptions={acTypeOptions} 
          flightRegOptions={flightRegOptions}
          depOptions={depOptions}
          arrOptions={arrOptions}
        />
      }      
    </Box>
  );
};

export default CrewRoutes;
